import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Users, User, ChevronDown, ChevronRight, Calculator, AlertTriangle, HardHat, CheckCircle, Clock, Pause, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { SelectedMemberForDelay } from '@/hooks/useDelayPenaltySimulation';
import { format, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useModalProtection } from '@/hooks/useModalProtection';

interface Equipe {
  id: string;
  nom: string;
  specialite?: string;
  pictogramme?: string;
  couleur?: string;
}

interface Membre {
  id: string;
  prenom: string;
  nom: string;
  poste?: string;
  taux_horaire: number;
  coefficient_charges: number;
  heures_par_jour: number;
  equipe_id?: string | null;
  statut?: 'sur_chantier' | 'disponible' | 'repos' | 'en_arret' | 'en_rotation';
}

// Interface pour les conflits détectés
interface ConflitMembre {
  membreId: string;
  chantierNom: string;
  dateDebut: string;
  dateFin: string;
}

interface ModalSelectionEquipeRetardProps {
  isOpen: boolean;
  onClose: () => void;
  entrepriseId?: string;
  chantierId?: string; // ID du chantier actuel (pour exclure de la détection de conflits)
  delayDays: number;
  dateDebutRetard?: Date; // Date de début du retard
  dateFinRetard?: Date;   // Date de fin du retard
  onConfirm: (selectedMembers: SelectedMemberForDelay[]) => void;
  onChange?: (selectedMembers: SelectedMemberForDelay[]) => void; // Mise à jour en temps réel
  selectedMembers?: SelectedMemberForDelay[];
  embedded?: boolean; // Mode intégré (sans Dialog wrapper)
}

// Exporter l'interface pour utilisation dans ModalRepriseChantier
export type { SelectedMemberForDelay };

export function ModalSelectionEquipeRetard({
  isOpen,
  onClose,
  entrepriseId: entrepriseIdProp,
  chantierId,
  delayDays,
  dateDebutRetard,
  dateFinRetard,
  onConfirm,
  onChange,
  selectedMembers = [],
  embedded = false,
}: ModalSelectionEquipeRetardProps) {
  const { user } = useAuth();
  useModalProtection(isOpen);
  const [equipes, setEquipes] = useState<Equipe[]>([]);
  const [membres, setMembres] = useState<Membre[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [expandedEquipes, setExpandedEquipes] = useState<Set<string>>(new Set());
  const [conflitsMembres, setConflitsMembres] = useState<Map<string, ConflitMembre>>(new Map());

  // Convertir les dates en strings pour stabiliser les dépendances
  const dateDebutStr = useMemo(() => dateDebutRetard ? format(dateDebutRetard, 'yyyy-MM-dd') : null, [dateDebutRetard?.getTime()]);
  const dateFinStr = useMemo(() => dateFinRetard ? format(dateFinRetard, 'yyyy-MM-dd') : null, [dateFinRetard?.getTime()]);

  // Charger les équipes et membres
  useEffect(() => {
    // En mode embedded, charger même si isOpen n'est pas utilisé
    if (!user) return;
    if (!embedded && !isOpen) return;

    const loadData = async () => {
      setLoading(true);
      if (import.meta.env.DEV) console.log('[ModalSelectionEquipeRetard] Chargement données...', { entrepriseIdProp, chantierId, dateDebutStr, dateFinStr, embedded });
      try {
        // Récupérer l'entreprise si non fournie
        let entrepriseId = entrepriseIdProp;
        if (!entrepriseId) {
          const { data: entreprise } = await supabase
            .from('entreprises')
            .select('id')
            .eq('proprietaire_user_id', user.id)
            .single();

          if (!entreprise) {
            console.error('[ModalSelectionEquipeRetard] Entreprise non trouvée');
            setLoading(false);
            return;
          }
          entrepriseId = entreprise.id;
        }

        if (import.meta.env.DEV) console.log('[ModalSelectionEquipeRetard] Entreprise ID:', entrepriseId);

        // Charger les équipes avec pictogramme et couleur
        const { data: equipesData, error: equipesError } = await supabase
          .from('equipes')
          .select('id, nom, specialite, pictogramme, couleur')
          .eq('entreprise_id', entrepriseId)
          .order('nom');

        if (equipesError) {
          console.error('[ModalSelectionEquipeRetard] Erreur chargement équipes:', equipesError);
        }

        // Charger les membres avec leur statut
        const { data: membresData, error: membresError } = await supabase
          .from('membres_equipe')
          .select('id, prenom, nom, poste, taux_horaire, coefficient_charges, heures_par_jour, equipe_id, statut')
          .eq('entreprise_id', entrepriseId)
          .order('nom');

        if (membresError) {
          console.error('[ModalSelectionEquipeRetard] Erreur chargement membres:', membresError);
        }

        if (import.meta.env.DEV) console.log('[ModalSelectionEquipeRetard] Équipes chargées:', equipesData?.length, 'Membres chargés:', membresData?.length);

        setEquipes(equipesData || []);
        setMembres(membresData || []);

        // Charger les conflits si les dates de retard sont fournies
        if (dateDebutStr && dateFinStr && membresData && membresData.length > 0) {
          if (import.meta.env.DEV) {
            console.log('[ModalSelectionEquipeRetard] Chargement conflits pour période:', dateDebutStr, '-', dateFinStr);
            console.log('[ModalSelectionEquipeRetard] chantierId exclu:', chantierId);
          }

          // Récupérer les affectations en conflit pour tous les membres
          let query = supabase
            .from('affectations_chantiers')
            .select(`
              id, membre_equipe_id, date_debut, date_fin, chantier_id,
              chantiers(nom_chantier, reference_chantier)
            `)
            .in('membre_equipe_id', membresData.map(m => m.id))
            .lte('date_debut', dateFinStr)
            .gte('date_fin', dateDebutStr);

          // Exclure le chantier actuel si fourni
          if (chantierId) {
            query = query.neq('chantier_id', chantierId);
          }

          const { data: conflits, error: conflitsError } = await query;

          if (conflitsError) {
            console.error('[ModalSelectionEquipeRetard] ❌ Erreur chargement conflits:', conflitsError);
          } else {
            if (import.meta.env.DEV) console.log('[ModalSelectionEquipeRetard] Conflits trouvés:', conflits?.length || 0, conflits);
          }

          // Construire la map des conflits
          const conflitsMap = new Map<string, ConflitMembre>();
          if (conflits) {
            for (const conflit of conflits) {
              conflitsMap.set(conflit.membre_equipe_id, {
                membreId: conflit.membre_equipe_id,
                chantierNom: (conflit.chantiers as any)?.nom_chantier || (conflit.chantiers as any)?.reference_chantier || 'Autre chantier',
                dateDebut: conflit.date_debut,
                dateFin: conflit.date_fin,
              });
            }
          }
          if (import.meta.env.DEV) console.log('[ModalSelectionEquipeRetard] conflitsMap size:', conflitsMap.size);
          setConflitsMembres(conflitsMap);
        } else {
          if (import.meta.env.DEV) console.log('[ModalSelectionEquipeRetard] Pas de vérification conflits - dates manquantes:', { dateDebutStr, dateFinStr });
          // Réinitialiser les conflits si pas de dates
          setConflitsMembres(new Map());
        }
      } catch (error) {
        console.error('Erreur chargement données:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, user, entrepriseIdProp, chantierId, dateDebutStr, dateFinStr, embedded]);

  // Initialiser la sélection avec les membres déjà sélectionnés (seulement à l'ouverture)
  useEffect(() => {
    if ((isOpen || embedded) && selectedMembers.length > 0) {
      setSelectedMemberIds(new Set(selectedMembers.map(m => m.id)));
    }
  }, [isOpen, embedded]); // Ne dépend QUE de isOpen/embedded pour éviter les boucles

  // Grouper les membres par équipe
  const membresByEquipe = useMemo(() => {
    const grouped: Record<string, Membre[]> = { 'sans-equipe': [] };

    equipes.forEach(eq => {
      grouped[eq.id] = [];
    });

    membres.forEach(m => {
      if (m.equipe_id && grouped[m.equipe_id]) {
        grouped[m.equipe_id].push(m);
      } else {
        grouped['sans-equipe'].push(m);
      }
    });

    return grouped;
  }, [equipes, membres]);

  // Calculer le coût journalier d'un membre
  const getCoutJournalier = (membre: Membre) => {
    return membre.taux_horaire * (membre.coefficient_charges || 1.85) * (membre.heures_par_jour || 8);
  };

  // Calculer le statut dynamique d'un membre basé sur ses affectations aux dates de reprise
  // Un membre marqué "sur_chantier" en base mais SANS conflit aux dates entrées = disponible
  const getStatutDynamique = (membre: Membre): string | null => {
    // Si conflit détecté aux dates de reprise, le membre est réellement occupé
    if (conflitsMembres.has(membre.id)) {
      return 'sur_chantier';
    }

    // Si marqué "sur_chantier" mais pas de conflit aux dates de reprise = disponible pour ces dates
    if (membre.statut === 'sur_chantier') {
      return 'disponible';
    }

    // Sinon, retourner le statut statique (repos, en_arret, en_rotation, disponible)
    return membre.statut || null;
  };

  // Afficher le badge de statut d'un membre (utilise le statut dynamique)
  const getStatutBadge = (membre: Membre) => {
    const statutEffectif = getStatutDynamique(membre);
    if (!statutEffectif) return null;

    const statusConfig: Record<string, { label: string; icon: React.ReactNode; className: string }> = {
      disponible: {
        label: 'Disponible',
        icon: <CheckCircle className="h-3 w-3" />,
        className: 'border-green-500/50 text-green-600 bg-green-500/10',
      },
      sur_chantier: {
        label: 'Sur chantier',
        icon: <HardHat className="h-3 w-3" />,
        className: 'border-blue-500/50 text-blue-600 bg-blue-500/10',
      },
      repos: {
        label: 'Repos',
        icon: <Pause className="h-3 w-3" />,
        className: 'border-gray-500/50 text-gray-500 bg-gray-500/10',
      },
      en_arret: {
        label: 'Arrêt',
        icon: <Clock className="h-3 w-3" />,
        className: 'border-red-500/50 text-red-600 bg-red-500/10',
      },
      en_rotation: {
        label: 'Rotation',
        icon: <RefreshCw className="h-3 w-3" />,
        className: 'border-purple-500/50 text-purple-600 bg-purple-500/10',
      },
    };

    const config = statusConfig[statutEffectif];
    if (!config) return null;

    return (
      <Badge variant="outline" className={`text-[10px] flex items-center gap-1 ${config.className}`}>
        {config.icon}
        <span>{config.label}</span>
      </Badge>
    );
  };

  // Calculer le coût total des membres sélectionnés
  const totalCoutJournalier = useMemo(() => {
    return membres
      .filter(m => selectedMemberIds.has(m.id))
      .reduce((total, m) => total + getCoutJournalier(m), 0);
  }, [membres, selectedMemberIds]);

  const totalCoutRetard = totalCoutJournalier * delayDays;

  // Notifier le parent des changements en temps réel
  useEffect(() => {
    if (!onChange || membres.length === 0) return;

    const selected: SelectedMemberForDelay[] = membres
      .filter(m => selectedMemberIds.has(m.id))
      .map(m => {
        // Trouver l'équipe du membre pour récupérer pictogramme et couleur
        const equipe = m.equipe_id ? equipes.find(e => e.id === m.equipe_id) : null;
        // Vérifier si le membre a un conflit
        const conflit = conflitsMembres.get(m.id);
        return {
          id: m.id,
          prenom: m.prenom,
          nom: m.nom,
          taux_horaire: m.taux_horaire,
          coefficient_charges: m.coefficient_charges || 1.85,
          heures_par_jour: m.heures_par_jour || 8,
          equipe_id: m.equipe_id,
          equipe_pictogramme: (equipe as any)?.pictogramme || null,
          equipe_couleur: (equipe as any)?.couleur || null,
          hasConflict: !!conflit,
          conflictChantierNom: conflit?.chantierNom,
        };
      });

    onChange(selected);
  }, [selectedMemberIds, membres, equipes, conflitsMembres, onChange]);

  // Toggle un membre
  const toggleMembre = (membreId: string) => {
    setSelectedMemberIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(membreId)) {
        newSet.delete(membreId);
      } else {
        newSet.add(membreId);
      }
      return newSet;
    });
  };

  // Toggle tous les membres d'une équipe
  const toggleEquipe = (equipeId: string) => {
    const membresEquipe = membresByEquipe[equipeId] || [];
    const allSelected = membresEquipe.every(m => selectedMemberIds.has(m.id));

    setSelectedMemberIds(prev => {
      const newSet = new Set(prev);
      membresEquipe.forEach(m => {
        if (allSelected) {
          newSet.delete(m.id);
        } else {
          newSet.add(m.id);
        }
      });
      return newSet;
    });
  };

  // Toggle expansion d'une équipe
  const toggleExpand = (equipeId: string) => {
    setExpandedEquipes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(equipeId)) {
        newSet.delete(equipeId);
      } else {
        newSet.add(equipeId);
      }
      return newSet;
    });
  };

  // Vérifier si tous les membres d'une équipe sont sélectionnés
  const isEquipeFullySelected = (equipeId: string) => {
    const membresEquipe = membresByEquipe[equipeId] || [];
    return membresEquipe.length > 0 && membresEquipe.every(m => selectedMemberIds.has(m.id));
  };

  // Vérifier si certains membres d'une équipe sont sélectionnés
  const isEquipePartiallySelected = (equipeId: string) => {
    const membresEquipe = membresByEquipe[equipeId] || [];
    const selectedCount = membresEquipe.filter(m => selectedMemberIds.has(m.id)).length;
    return selectedCount > 0 && selectedCount < membresEquipe.length;
  };

  // Confirmer la sélection
  const handleConfirm = () => {
    const selectedMembers: SelectedMemberForDelay[] = membres
      .filter(m => selectedMemberIds.has(m.id))
      .map(m => {
        const equipe = m.equipe_id ? equipes.find(e => e.id === m.equipe_id) : null;
        return {
          id: m.id,
          prenom: m.prenom,
          nom: m.nom,
          taux_horaire: m.taux_horaire,
          coefficient_charges: m.coefficient_charges || 1.85,
          heures_par_jour: m.heures_par_jour || 8,
          equipe_id: m.equipe_id,
          equipe_pictogramme: equipe?.pictogramme || null,
          equipe_couleur: equipe?.couleur || null,
        };
      });

    // Ne pas appeler onClose() ici car onConfirm ferme déjà la modal
    // Cela évite que onClose du parent soit appelé avec un state pas encore mis à jour
    onConfirm(selectedMembers);
  };

  // Contenu principal (utilisable en mode embedded ou dialog)
  const renderContent = () => (
    <>
      <ScrollArea className={embedded ? "h-[300px] pr-4" : "h-[350px] pr-4"}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <span className="text-muted-foreground">Chargement...</span>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Équipes */}
            {equipes.map(equipe => {
              const membresEquipe = membresByEquipe[equipe.id] || [];
              if (membresEquipe.length === 0) return null;

              const isExpanded = expandedEquipes.has(equipe.id);
              const isFullySelected = isEquipeFullySelected(equipe.id);
              const isPartiallySelected = isEquipePartiallySelected(equipe.id);
              const coutEquipe = membresEquipe.reduce((t, m) => t + getCoutJournalier(m), 0);

              return (
                <div key={equipe.id} className="border rounded-lg">
                  {/* Header équipe */}
                  <div
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50"
                    onClick={() => toggleExpand(equipe.id)}
                  >
                    <Checkbox
                      checked={isFullySelected}
                      ref={(el) => {
                        if (el && isPartiallySelected) {
                          (el as any).indeterminate = true;
                        }
                      }}
                      onCheckedChange={() => toggleEquipe(equipe.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                    <Users className="h-4 w-4 text-primary" />
                    <span className="font-medium flex-1">{equipe.nom}</span>
                    <Badge variant="outline" className="text-xs">
                      {membresEquipe.length} membre{membresEquipe.length > 1 ? 's' : ''}
                    </Badge>
                    <span className="text-sm text-muted-foreground font-mono">
                      {coutEquipe.toFixed(0)} €/j
                    </span>
                  </div>

                  {/* Liste des membres de l'équipe */}
                  {isExpanded && (
                    <div className="border-t bg-muted/20 p-2 space-y-1">
                      {membresEquipe.map(membre => {
                        const conflit = conflitsMembres.get(membre.id);
                        return (
                          <div
                            key={membre.id}
                            className={`flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer ${conflit ? 'bg-orange-500/5' : ''}`}
                            onClick={() => toggleMembre(membre.id)}
                          >
                            <Checkbox
                              checked={selectedMemberIds.has(membre.id)}
                              onCheckedChange={() => toggleMembre(membre.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <User className={`h-4 w-4 ${conflit ? 'text-orange-500' : 'text-muted-foreground'}`} />
                            <span className="flex-1 flex items-center gap-2">
                              <span>
                                {membre.prenom} {membre.nom}
                                {membre.poste && (
                                  <span className="text-muted-foreground text-xs ml-2">
                                    ({membre.poste})
                                  </span>
                                )}
                              </span>
                              {!conflit && getStatutBadge(membre)}
                            </span>
                            {conflit && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span>
                                      <Badge variant="outline" className="text-[10px] border-orange-500/50 text-orange-500 bg-orange-500/10 flex items-center gap-1 max-w-[180px]">
                                        <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                                        <span className="truncate">{conflit.chantierNom}</span>
                                        <span className="flex-shrink-0">→ {format(new Date(conflit.dateFin), 'dd/MM')}</span>
                                      </Badge>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="p-3 space-y-2">
                                    <p className="text-xs text-muted-foreground font-medium">Affectation en cours</p>
                                    <div className="flex items-center gap-2">
                                      <HardHat className="h-4 w-4 text-amber-500" />
                                      <span className="font-medium text-amber-500">{conflit.chantierNom}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                      Jusqu'au {format(new Date(conflit.dateFin), 'dd MMMM yyyy', { locale: fr })}
                                    </p>
                                    <p className="text-xs text-green-500 font-medium">
                                      Disponible le {format(addDays(new Date(conflit.dateFin), 1), 'dd/MM')}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <span className="text-sm text-muted-foreground font-mono">
                              {getCoutJournalier(membre).toFixed(0)} €/j
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Membres sans équipe */}
            {membresByEquipe['sans-equipe'].length > 0 && (
              <div className="border rounded-lg">
                <div
                  className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleExpand('sans-equipe')}
                >
                  <Checkbox
                    checked={isEquipeFullySelected('sans-equipe')}
                    onCheckedChange={() => toggleEquipe('sans-equipe')}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {expandedEquipes.has('sans-equipe') ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium flex-1 text-muted-foreground">Sans équipe</span>
                  <Badge variant="outline" className="text-xs">
                    {membresByEquipe['sans-equipe'].length} membre{membresByEquipe['sans-equipe'].length > 1 ? 's' : ''}
                  </Badge>
                </div>

                {expandedEquipes.has('sans-equipe') && (
                  <div className="border-t bg-muted/20 p-2 space-y-1">
                    {membresByEquipe['sans-equipe'].map(membre => {
                      const conflit = conflitsMembres.get(membre.id);
                      return (
                        <div
                          key={membre.id}
                          className={`flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer ${conflit ? 'bg-orange-500/5' : ''}`}
                          onClick={() => toggleMembre(membre.id)}
                        >
                          <Checkbox
                            checked={selectedMemberIds.has(membre.id)}
                            onCheckedChange={() => toggleMembre(membre.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <User className={`h-4 w-4 ${conflit ? 'text-orange-500' : 'text-muted-foreground'}`} />
                          <span className="flex-1 flex items-center gap-2">
                            <span>
                              {membre.prenom} {membre.nom}
                              {membre.poste && (
                                <span className="text-muted-foreground text-xs ml-2">
                                  ({membre.poste})
                                </span>
                              )}
                            </span>
                            {!conflit && getStatutBadge(membre)}
                          </span>
                          {conflit && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Badge variant="outline" className="text-[10px] border-orange-500/50 text-orange-500 bg-orange-500/10 flex items-center gap-1 max-w-[180px]">
                                      <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                                      <span className="truncate">{conflit.chantierNom}</span>
                                      <span className="flex-shrink-0">→ {format(new Date(conflit.dateFin), 'dd/MM')}</span>
                                    </Badge>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="p-3 space-y-2">
                                  <p className="text-xs text-muted-foreground font-medium">Affectation en cours</p>
                                  <div className="flex items-center gap-2">
                                    <HardHat className="h-4 w-4 text-amber-500" />
                                    <span className="font-medium text-amber-500">{conflit.chantierNom}</span>
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    Jusqu'au {format(new Date(conflit.dateFin), 'dd MMMM yyyy', { locale: fr })}
                                  </p>
                                  <p className="text-xs text-green-500 font-medium">
                                    Disponible le {format(addDays(new Date(conflit.dateFin), 1), 'dd/MM')}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          <span className="text-sm text-muted-foreground font-mono">
                            {getCoutJournalier(membre).toFixed(0)} €/j
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      {/* Résumé du coût */}
      <div className={embedded ? "pt-4" : "border-t pt-4"}>
        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm">
              <Calculator className="h-4 w-4" />
              Coût journalier sélectionné
            </span>
            <span className="font-mono font-semibold">{totalCoutJournalier.toFixed(2)} €/jour</span>
          </div>
          <div className="flex items-center justify-between text-lg">
            <span className="font-medium">
              Coût total ({delayDays} jour{delayDays > 1 ? 's' : ''})
            </span>
            <span className="font-mono font-bold text-primary">{totalCoutRetard.toFixed(2)} €</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {selectedMemberIds.size} membre{selectedMemberIds.size > 1 ? 's' : ''} sélectionné{selectedMemberIds.size > 1 ? 's' : ''}
          </p>
        </div>
      </div>
    </>
  );

  // Mode embedded : rendre directement le contenu sans Dialog
  if (embedded) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="h-4 w-4" />
          Sélectionnez les membres pour la reprise du chantier
        </div>
        {renderContent()}
      </div>
    );
  }

  // Mode normal : rendre dans un Dialog
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent variant="industrial" className="sm:max-w-[600px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Affecter l'équipe au retard
          </DialogTitle>
          <DialogDescription>
            Sélectionnez les équipes ou membres qui travailleront pendant les <strong>{delayDays} jours</strong> de retard.
            Le coût sera recalculé en fonction de votre sélection.
          </DialogDescription>
        </DialogHeader>

        {renderContent()}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} className="text-white/30 bg-transparent hover:text-white/55 hover:bg-transparent">
            Annuler
          </Button>
          <Button onClick={handleConfirm} disabled={selectedMemberIds.size === 0} className="bg-gradient-to-r from-[#F7B500] to-[#FF9500] text-black font-bold hover:brightness-110 shadow-[0_6px_20px_rgba(247,181,0,0.2)]">
            Appliquer la sélection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
